import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardContent,
    Chip,
    FormControlLabel,
    FormGroup,
    Paper,
    Switch,
    Table,
    TableBody,
    TableContainer,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateTime, Duration } from 'luxon';

const formatHours = (ms) => {
    if (!ms || ms <= 0) return '0h 0m';
    const dur = Duration.fromMillis(ms).shiftTo('hours', 'minutes');
    return `${ Math.floor(dur.hours) }h ${ Math.floor(dur.minutes) }m`;
};

const MIN_SHIFT_MS = 60 * 60 * 1000;       // 1 hour
const MAX_SHIFT_MS = 8 * 60 * 60 * 1000;  // 8 hours

const formatTimestamp = (ts) => {
    if (!ts) return '—';
    return DateTime.fromMillis(ts).toFormat('MMM dd, hh:mm a');
};

export const HoursReport = ({
    processedShifts,
    patrollerMap,
    hoursPage,
    hoursRowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    getPatrollerNameFormatted
}) => {
    const [ showUnder1h, setShowUnder1h ] = React.useState(false);
    const [ showOver8h, setShowOver8h ] = React.useState(false);

    const safeProcessedShifts = processedShifts || [];

    // Build per-patroller hours data from shift timelogs
    const patrollerHoursMap = React.useMemo(() => {
        const map = new Map();

        safeProcessedShifts.forEach(shift => {
            const timelogs = shift.timelogs || [];

            timelogs.forEach(log => {
                const pid = log.patroller;
                if (!pid) return;

                const patrollerInfo = patrollerMap.get(pid);
                if (!map.has(pid)) {
                    map.set(pid, {
                        id: pid,
                        name: getPatrollerNameFormatted(pid),
                        lastName: patrollerInfo?.lastName || '',
                        firstName: patrollerInfo?.firstName || '',
                        totalMs: 0,
                        shifts: []
                    });
                }

                const entry = map.get(pid);
                const started = log.started || null;
                const stopped = log.stopped || null;
                const durationMs = (started && stopped) ? stopped - started : null;

                if (durationMs && durationMs > 0) {
                    entry.totalMs += durationMs;
                }

                entry.shifts.push({
                    shiftId: shift.id,
                    shiftName: shift.name,
                    date: shift.date,
                    started,
                    stopped,
                    durationMs
                });
            });
        });

        return map;
    }, [ safeProcessedShifts, patrollerMap, getPatrollerNameFormatted ]);

    const sortedPatrollers = React.useMemo(() => {
        return Array.from(patrollerHoursMap.values())
            .filter(p => {
                if (!showUnder1h && !showOver8h) return true;
                const hasUnder = p.shifts.some(s => s.durationMs && s.durationMs < MIN_SHIFT_MS);
                const hasOver = p.shifts.some(s => s.durationMs && s.durationMs > MAX_SHIFT_MS);
                if (showUnder1h && showOver8h) return hasUnder || hasOver;
                if (showUnder1h) return hasUnder;
                if (showOver8h) return hasOver;
            })
            .sort((a, b) => {
                if (a.lastName !== b.lastName) return a.lastName.localeCompare(b.lastName);
                return a.firstName.localeCompare(b.firstName);
            });
    }, [ patrollerHoursMap, showUnder1h, showOver8h ]);

    const paginatedPatrollers = React.useMemo(() =>
        sortedPatrollers.slice(
            hoursPage * hoursRowsPerPage,
            hoursPage * hoursRowsPerPage + hoursRowsPerPage
        ),
        [ sortedPatrollers, hoursPage, hoursRowsPerPage ]
    );

    const totalPatrollers = sortedPatrollers.length;
    const totalMs = sortedPatrollers.reduce((sum, p) => sum + p.totalMs, 0);
    const shortShifts = sortedPatrollers.reduce((count, p) =>
        count + p.shifts.filter(s => s.durationMs && s.durationMs < MIN_SHIFT_MS).length, 0);
    const longShifts = sortedPatrollers.reduce((count, p) =>
        count + p.shifts.filter(s => s.durationMs && s.durationMs > MAX_SHIFT_MS).length, 0);
    const validShifts = sortedPatrollers.flatMap(p => p.shifts.filter(s => s.durationMs && s.durationMs > MIN_SHIFT_MS));
    const avgShiftMs = validShifts.length > 0
        ? validShifts.reduce((sum, s) => sum + s.durationMs, 0) / validShifts.length
        : 0;

    return (
        <Box>
            <Box sx={ { display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' } }>
                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="primary">{ totalPatrollers }</Typography>
                        <Typography variant="body2" color="text.secondary">Patrollers with Timelogs</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color={ shortShifts > 0 ? 'error.main' : 'text.primary' }>{ shortShifts }</Typography>
                        <Typography variant="body2" color="text.secondary">Shifts Under 1 Hour</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 160 } }>
                    <CardContent>
                        <Typography variant="h6" color="primary">{ avgShiftMs > 0 ? formatHours(avgShiftMs) : '—' }</Typography>
                        <Typography variant="body2" color="text.secondary">Avg Hours per Shift (≥ 1h)</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color={ longShifts > 0 ? 'warning.main' : 'text.primary' }>{ longShifts }</Typography>
                        <Typography variant="body2" color="text.secondary">Shifts Over 8 Hours</Typography>
                    </CardContent>
                </Card>

                <Card sx={ { minWidth: 160 } }>
                    <CardContent>
                        <Typography variant="h6" color="primary">{ formatHours(totalMs) }</Typography>
                        <Typography variant="body2" color="text.secondary">Total Hours</Typography>
                    </CardContent>
                </Card>
            </Box>

            <FormGroup row sx={ { mt: 2, mb: 1 } }>
                <FormControlLabel
                    control={
                        <Switch
                            checked={ showUnder1h }
                            onChange={ (e) => setShowUnder1h(e.target.checked) }
                            color="error"
                        />
                    }
                    label="Show Under 1 Hour Only"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={ showOver8h }
                            onChange={ (e) => setShowOver8h(e.target.checked) }
                            color="warning"
                        />
                    }
                    label="Show Over 8 Hours Only"
                />
            </FormGroup>

            { totalPatrollers === 0 && (
                <Typography color="text.secondary" sx={ { mt: 2 } }>
                    No timelog data recorded for this event.
                </Typography>
            ) }

            { paginatedPatrollers.map((patroller) => (
                <Accordion key={ patroller.id }>
                    <AccordionSummary
                        expandIcon={ <ExpandMoreIcon /> }
                        sx={ { '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden' } } }
                    >
                        <Box sx={ { display: 'flex', alignItems: 'center', gap: 2, width: '100%', minWidth: 0 } }>
                            <Typography variant="h6" sx={ {
                                flex: '1 1 0',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            } }>
                                { patroller.name }
                            </Typography>
                            <Chip
                                label={ formatHours(patroller.totalMs) }
                                color={ (() => {
                                    const hasUnder = patroller.shifts.some(s => s.durationMs && s.durationMs < MIN_SHIFT_MS);
                                    const hasOver = patroller.shifts.some(s => s.durationMs && s.durationMs > MAX_SHIFT_MS);
                                    if (hasUnder) return 'error';
                                    if (hasOver) return 'warning';
                                    return patroller.totalMs > 0 ? 'success' : 'default';
                                })() }
                                size="small"
                            />
                            <Typography variant="body2" color="text.secondary">
                                { patroller.shifts.length } shift{ patroller.shifts.length !== 1 ? 's' : '' }
                            </Typography>
                        </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                        <TableContainer component={ Paper }>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Typography variant="body1">Shift</Typography></TableCell>
                                        <TableCell><Typography variant="body1">Clock In</Typography></TableCell>
                                        <TableCell><Typography variant="body1">Clock Out</Typography></TableCell>
                                        <TableCell><Typography variant="body1">Hours</Typography></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    { patroller.shifts
                                        .slice()
                                        .sort((a, b) => Date.parse(a.date) - Date.parse(b.date))
                                        .map((s, index) => {
                                            const isIncomplete = s.started && !s.stopped;
                                            const isUnder = s.durationMs && s.durationMs < MIN_SHIFT_MS;
                                            const isOver = s.durationMs && s.durationMs > MAX_SHIFT_MS;

                                            return (
                                                <TableRow
                                                    key={ index }
                                                    sx={ isUnder
                                                        ? { backgroundColor: 'error.dark' }
                                                        : isOver
                                                            ? { backgroundColor: 'warning.dark' }
                                                            : undefined
                                                    }
                                                >
                                                    <TableCell>{ s.shiftName }</TableCell>
                                                    <TableCell>{ formatTimestamp(s.started) }</TableCell>
                                                    <TableCell>
                                                        { isIncomplete
                                                            ? <Chip label="In Progress" color="warning" size="small" />
                                                            : formatTimestamp(s.stopped)
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                                                            { s.durationMs
                                                                ? formatHours(s.durationMs)
                                                                : isIncomplete ? '—' : 'No data'
                                                            }
                                                            { isUnder && <Chip label="< 1h" color="error" size="small" /> }
                                                            { isOver && <Chip label="> 8h" color="warning" size="small" /> }
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }) }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            )) }

            <TablePagination
                component="div"
                count={ sortedPatrollers.length }
                page={ hoursPage }
                onPageChange={ onPageChange }
                rowsPerPage={ hoursRowsPerPage }
                onRowsPerPageChange={ onRowsPerPageChange }
                rowsPerPageOptions={ [ 5, 10, 25, 50 ] }
            />
        </Box>
    );
};
